import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { gql } from "@apollo/client";

// Define the mutation
const UPDATE_KYC_OTP_MUTATION = gql`
  mutation Mutation($input: KYCOtpUpdateInput!) {
    updateKycOtp(input: $input) {
      success
      message
      error
    }
  }
`;

// Define TypeScript interfaces
interface KYCOtpUpdateInput {
  requestId: string;
  otp: string;
  reason?: string;
  status?: string;
}

/**
 * The queue states this dialog can move a request into.
 *
 * They are stored in kyc_details.portal_status, which already holds this family
 * ("In Progress", "Parked", "Closed") — NOT kyc_status, which is whether the KYC
 * itself passed. The two are different questions and conflating them would make
 * an assigned request read as approved.
 */
const STATUS_OPTIONS = ["ASSIGNED", "IN PROGRESS", "PENDING", "COMPLETED"];

interface UpdateKycOtpResponse {
  updateKycOtp: {
    success: boolean;
    message: string;
    error: string | null;
  };
}

interface UpdateRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (formData: { otp: string; reason: string; status: string }) => void;
  requestId: string;
  vin: string;
  refetchData?: () => void; // Optional refetch function for parent component
}

const UpdateRequestDialog: React.FC<UpdateRequestDialogProps> = ({
  open,
  onClose,
  onSubmit,
  requestId,
  vin,
  refetchData,
}) => {
  const [otp, setOtp] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("ASSIGNED");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  // Use the mutation hook
  const [updateKycOtp, { loading }] = useMutation<UpdateKycOtpResponse>(
    UPDATE_KYC_OTP_MUTATION,
    {
      onCompleted: (data) => {
        if (data.updateKycOtp.success) {
          // Show success message
          setSnackbar({
            open: true,
            message: data.updateKycOtp.message || "OTP updated successfully!",
            severity: "success",
          });
          
          // Call parent's onSubmit if provided (for backward compatibility)
          if (onSubmit) {
            onSubmit({ otp, reason, status });
          }
          
          // Refetch data if provided
          if (refetchData) {
            refetchData();
          }
          
          // Close dialog after successful submission
          setTimeout(() => {
            onClose();
            // Reset form
            setOtp("");
            setReason("");
            setStatus("ASSIGNED");
          }, 1000);
        } else {
          // Show error message from server
          setSnackbar({
            open: true,
            message: data.updateKycOtp.error || "Failed to update OTP",
            severity: "error",
          });
        }
      },
      onError: (error) => {
        // Show error message for network or other errors
        setSnackbar({
          open: true,
          message: error.message || "An error occurred while updating OTP",
          severity: "error",
        });
      },
    }
  );

  const handleSubmit = async () => {
    // Validate OTP
    if (!otp.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter OTP",
        severity: "error",
      });
      return;
    }

    // Prepare mutation input
    const input: KYCOtpUpdateInput = {
      requestId,
      otp: otp.trim(),
      reason: reason.trim() || undefined,
      status: status || undefined,
    };

    try {
      // Execute the mutation
      await updateKycOtp({
        variables: { input },
      });
    } catch (error) {
      // Error handling is done in onError callback
      console.error("Mutation error:", error);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClose = () => {
    // Reset form when closing
    setOtp("");
    setReason("");
    setStatus("ASSIGNED");
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Update Request
          <IconButton onClick={handleClose} size="small" sx={{ color: '#000' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <TextField
            label="Request ID"
            value={requestId}
            fullWidth
            margin="dense"
            disabled
          />
          <TextField
            label="VIN"
            value={vin}
            fullWidth
            margin="dense"
            disabled
          />
          <TextField
            label="OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter OTP"
            fullWidth
            margin="dense"
            required
            inputProps={{ maxLength: 6 }} 
            error={!otp.trim()}
            helperText={!otp.trim() ? "OTP is required" : ""}
          />
          {/* Reason and Status were commented out because submitting them was
              impossible: the mutation sent `requestId` to an input that required
              `kyc_id`, so every submission failed GraphQL validation before it
              reached the server — `otp` is populated on 0 of 31,403 rows. The
              input now accepts either key and carries both fields, so they are
              live. */}
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter Reason"
            fullWidth
            margin="dense"
          />
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            fullWidth
            margin="dense"
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>{o}</MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions sx={{ pb: '2%', px: '2%', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="error"
            disabled={loading || !otp.trim()}
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UpdateRequestDialog;